import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles } from '@/mocks/articles';

const articleDetailImages: Record<string, string> = {
  'article-entertainment-01': 'https://readdy.ai/api/search-image?query=Minimalist%20editorial%20photography%20with%20warm%20neutral%20beige%20background%2C%20clean%20composition%20with%20microphone%20and%20spotlight%2C%20soft%20natural%20lighting%2C%20modern%20entertainment%20aesthetic%2C%20high%20detail%2C%20elegant%20atmosphere&width=1200&height=675&seq=detail-entertainment-01&orientation=landscape',
  'article-society-01': 'https://readdy.ai/api/search-image?query=Minimalist%20editorial%20photography%20with%20warm%20neutral%20beige%20background%2C%20clean%20composition%20with%20cityscape%20silhouette%2C%20soft%20natural%20lighting%2C%20modern%20lifestyle%20aesthetic%2C%20high%20detail%2C%20elegant%20atmosphere&width=1200&height=675&seq=detail-society-01&orientation=landscape',
  'article-internet-01': 'https://readdy.ai/api/search-image?query=Minimalist%20editorial%20photography%20with%20warm%20neutral%20beige%20background%2C%20clean%20composition%20with%20social%20media%20icons%20and%20network%20visualization%2C%20soft%20natural%20lighting%2C%20modern%20digital%20aesthetic%2C%20high%20detail%2C%20elegant%20atmosphere&width=1200&height=675&seq=detail-internet-01&orientation=landscape',
  'article-tech-01': 'https://readdy.ai/api/search-image?query=Minimalist%20editorial%20photography%20with%20warm%20neutral%20beige%20background%2C%20clean%20composition%20with%20laptop%20and%20gadgets%20on%20wooden%20desk%2C%20soft%20natural%20lighting%2C%20modern%20tech%20aesthetic%2C%20high%20detail%2C%20elegant%20atmosphere&width=1200&height=675&seq=detail-tech-01&orientation=landscape',
  'article-entertainment-02': 'https://readdy.ai/api/search-image?query=Minimalist%20editorial%20photography%20with%20warm%20neutral%20beige%20background%2C%20clean%20composition%20with%20stage%20curtain%20and%20spotlight%20glow%2C%20soft%20natural%20lighting%2C%20modern%20performance%20aesthetic%2C%20high%20detail%2C%20elegant%20atmosphere&width=1200&height=675&seq=detail-entertainment-02&orientation=landscape',
  'article-fashion-01': 'https://readdy.ai/api/search-image?query=Minimalist%20editorial%20photography%20with%20warm%20neutral%20beige%20background%2C%20clean%20composition%20with%20fabric%20textures%20and%20accessories%2C%20soft%20natural%20lighting%2C%20modern%20fashion%20aesthetic%2C%20high%20detail%2C%20elegant%20atmosphere&width=1200&height=675&seq=detail-fashion-01&orientation=landscape',
  'article-esports-01': 'https://readdy.ai/api/search-image?query=Minimalist%20editorial%20photography%20with%20warm%20neutral%20beige%20background%2C%20clean%20composition%20with%20gaming%20controller%20and%20colorful%20LED%20lights%2C%20soft%20natural%20lighting%2C%20modern%20esports%20aesthetic%2C%20high%20detail%2C%20elegant%20atmosphere&width=1200&height=675&seq=detail-esports-01&orientation=landscape',
  'article-finance-01': 'https://readdy.ai/api/search-image?query=Minimalist%20editorial%20photography%20with%20warm%20neutral%20beige%20background%2C%20clean%20composition%20with%20coins%20and%20growth%20chart%2C%20soft%20natural%20lighting%2C%20modern%20finance%20aesthetic%2C%20high%20detail%2C%20elegant%20atmosphere&width=1200&height=675&seq=detail-finance-01&orientation=landscape',
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(
    () => articles.find((a) => a.slug === slug),
    [slug]
  );

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.category === article.category && a.id !== article.id)
      .slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-background-200 rounded-full">
              <i className="ri-file-unknow-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-heading font-semibold text-foreground-900 mb-2">文章不存在</h1>
            <p className="text-sm text-foreground-500 mb-4">您要查找的文章可能已被删除或链接失效</p>
            <Link
              to="/"
              className="px-5 py-2 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors inline-block cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: article.categoryName, to: `/category/${article.category}` },
    { label: article.title },
  ];

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <Breadcrumb items={breadcrumbItems} />

        <article className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full hover:bg-primary-200 transition-colors cursor-pointer"
                >
                  {article.categoryName}
                </Link>
                <span className="text-xs text-foreground-400">{article.publishedAt}</span>
                <span className="flex items-center gap-1 text-xs text-foreground-400">
                  <i className="ri-eye-line"></i> {article.readCount.toLocaleString()} 阅读
                </span>
              </div>

              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-4 leading-tight">
                {article.title}
              </h1>

              <div className="flex items-center gap-3 pb-6 border-b border-background-200/70">
                <span className="text-sm text-foreground-500">作者：{article.author}</span>
              </div>
            </div>

            <div className="mb-8 rounded-lg overflow-hidden">
              <img
                src={articleDetailImages[article.imageSeq]}
                alt={article.title}
                className="w-full aspect-[16/9] object-cover"
              />
            </div>

            <div
              className="prose-custom text-sm md:text-base text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-8 pt-6 border-t border-background-200/70">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8">
              相关<strong className="text-primary-600">文章推荐</strong>
            </h2>

            {relatedArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300/50 transition-all duration-300 group cursor-pointer"
                  >
                    <div className="aspect-[16/10] overflow-hidden bg-background-200">
                      <img
                        src={articleDetailImages[ra.imageSeq]}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4 md:p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs rounded-full">
                          {ra.categoryName}
                        </span>
                        <span className="text-xs text-foreground-400">{ra.publishedAt}</span>
                      </div>
                      <h3 className="text-sm md:text-base font-heading font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {ra.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <p className="text-sm text-foreground-500 text-center py-8">暂无相关文章</p>
            )}
          </div>
        </section>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: article.title,
              description: article.excerpt,
              author: {
                '@type': 'Person',
                name: article.author,
              },
              datePublished: article.publishedAt,
              articleSection: article.categoryName,
              keywords: article.tags.join(','),
            }),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'https://heiliaowang.com',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: article.categoryName,
                  item: `https://heiliaowang.com/category/${article.category}`,
                },
                {
                  '@type': 'ListItem',
                  position: 3,
                  name: article.title,
                },
              ],
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}